import { find, map, without } from 'lodash-es'
import { isGroupId } from '#app/lib/boolean_tests'
import { newError } from '#app/lib/error'
import { getColorSquareDataUriFromCouchUuId } from '#app/lib/images/images'
import { treq } from '#app/lib/treq'
import { fixedEncodeURIComponent } from '#app/lib/utils'
import type { GroupMembershipAction } from '#groups/lib/groups_data'
import type { RelativeUrl } from '#server/types/common'
import type { ControllersAPI } from '#server/types/controllers_api'
import type { Group, GroupId, GroupMembershipCategory, GroupSlug } from '#server/types/group'
import type { GroupImagePath, ImageDataUrl } from '#server/types/image'
import type { UserId } from '#server/types/user'
import { mainUser } from '#user/lib/main_user'
import { getCachedSerializedUsers } from '#users/helpers'
import { serializeUser } from '#users/lib/users'
import { getUserById, type ExtendedUser, type UserSearchResult } from '#users/users_data'
import type { OverrideProperties } from 'type-fest'

interface SerializedGroupExtra {
  canonical: RelativeUrl
  pathname: RelativeUrl
  inventoryPathname: RelativeUrl
  listingsPathname: RelativeUrl
  settingsPathname: RelativeUrl
  mainUserIsAdmin: boolean
  mainUserIsMember: boolean
  mainUserStatus: ReturnType<typeof getUserGroupStatus>
}

interface SerializedGroupOverrides {
  // TODO: user another attribute to avoid the need to override
  picture: GroupImagePath | ImageDataUrl
}

export type SerializedGroup = OverrideProperties<Group, SerializedGroupOverrides> & SerializedGroupExtra

export function getAllGroupMembersIds (group: Group | SerializedGroup) {
  const { admins, members } = group
  return map([ ...admins, ...members ], 'user')
}

const getGroupInvitedUsersIds = (group: Group | SerializedGroup) => map(group.invited, 'user')
const getGroupRequestingUsersIds = (group: Group | SerializedGroup) => map(group.requested, 'user')

export function getGroupMembersCount (group: Group | SerializedGroup) {
  return group.admins.length + group.members.length
}

export function getGroupPicture (group: Group | SerializedGroup) {
  return group.picture || getColorSquareDataUriFromCouchUuId(group._id)
}

export function getGroupPathname (group: Group | SerializedGroup) {
  return `/groups/${group.slug}`
}

const memberIsMainUser = ({ user }) => user === mainUser?._id

export function mainUserIsGroupAdmin (group: Group | SerializedGroup) {
  return group.admins.find(memberIsMainUser) != null
}

function mainUserIsGroupNonAdminMember (group: Group | SerializedGroup) {
  return group.members.find(memberIsMainUser) != null
}

export function mainUserIsGroupMember (group: Group | SerializedGroup) {
  return mainUserIsGroupAdmin(group) || mainUserIsGroupNonAdminMember(group)
}

export function getUserGroupStatus (userId: UserId, group: Group | SerializedGroup) {
  if (getAllGroupMembersIds(group).includes(userId)) return 'member'
  if (getGroupInvitedUsersIds(group).includes(userId)) return 'invited'
  if (getGroupRequestingUsersIds(group).includes(userId)) return 'requested'
  return 'none'
}

export const getMainUserGroupStatus = (group: Group | SerializedGroup) => getUserGroupStatus(mainUser?._id, group)

export async function getCachedSerializedGroupMembers (group: Group | SerializedGroup) {
  const allMembersIds = getAllGroupMembersIds(group)
  return getCachedSerializedUsers(allMembersIds)
}

type MapIndex = number

export function serializeGroup (group: (Group & Partial<SerializedGroupExtra>) | SerializedGroup, options?: { refresh: boolean } | MapIndex) {
  if ('pathname' in group && !(typeof options === 'object' && options.refresh)) return group as SerializedGroup
  const slug = fixedEncodeURIComponent(group.slug)
  const base = `/groups/${slug}`
  group.picture ??= getColorSquareDataUriFromCouchUuId(group._id)
  const mainUserIsAdmin = mainUserIsGroupAdmin(group)
  return {
    ...group,
    canonical: base,
    pathname: base,
    inventoryPathname: `${base}/inventory`,
    listingsPathname: `${base}/lists`,
    settingsPathname: `${base}/settings`,
    mainUserIsAdmin,
    mainUserIsMember: mainUserIsAdmin || mainUserIsGroupNonAdminMember(group),
    mainUserStatus: getMainUserGroupStatus(group),
  } as SerializedGroup
}

export async function getGroup (groupId: GroupId) {
  const { group } = await treq('groups', 'get', 'by-id', { id: groupId })
  return serializeGroup(group)
}

export const getGroupById = getGroup

export async function getGroupBySlug (slug: string) {
  const { group } = await treq('groups', 'get', 'by-slug', { slug })
  return serializeGroup(group)
}

function findMembership (group: Group | SerializedGroup, category: GroupMembershipCategory, userId: UserId) {
  return find(group[category], { user: userId })
}

export function findInvitation (group: Group | SerializedGroup, userId: UserId) {
  return findMembership(group, 'invited', userId)
}

export async function findMainUserInvitor (group: Group | SerializedGroup) {
  const invitation = findInvitation(group, mainUser?._id)
  if (invitation && 'invitor' in invitation) return getUserById(invitation.invitor as UserId)
}

interface UpdateGroupSettingParams extends Pick<ControllersAPI['groups']['put']['update-settings']['params'], 'attribute' | 'value'> {
  groupId: GroupId
}

export async function updateGroupSetting ({ groupId, attribute, value }: UpdateGroupSettingParams) {
  return treq('groups', 'put', 'update-settings', {
    group: groupId,
    attribute,
    value,
  })
}

async function groupAction (action: GroupMembershipAction, groupId: GroupId, targetUserId?: UserId) {
  await treq('groups', 'put', action, {
    group: groupId,
    user: targetUserId,
  })
}

export function acceptGroupInvitation ({ groupId }: { groupId: GroupId }) {
  return groupAction('accept', groupId)
}
export function declineGroupInvitation ({ groupId }: { groupId: GroupId }) {
  return groupAction('decline', groupId)
}
export function requestToJoinGroup ({ groupId }: { groupId: GroupId }) {
  return groupAction('request', groupId)
}
export function cancelJoinGroupRequest ({ groupId }: { groupId: GroupId }) {
  return groupAction('cancel-request', groupId)
}
export function inviteUserToJoinGroup ({ groupId, invitedUserId }: { groupId: GroupId, invitedUserId: UserId }) {
  return groupAction('invite', groupId, invitedUserId)
}
export function acceptRequestToJoinGroup ({ groupId, requesterId }: { groupId: GroupId, requesterId: UserId }) {
  return groupAction('accept-request', groupId, requesterId)
}
export function refuseRequestToJoinGroup ({ groupId, requesterId }: { groupId: GroupId, requesterId: UserId }) {
  return groupAction('refuse-request', groupId, requesterId)
}
export function makeUserGroupAdmin ({ groupId, userId }: { groupId: GroupId, userId: UserId }) {
  return groupAction('make-admin', groupId, userId)
}
export function kickUserOutOfGroup ({ groupId, userId }: { groupId: GroupId, userId: UserId }) {
  return groupAction('kick', groupId, userId)
}
export function leaveGroup ({ groupId }: { groupId: GroupId }) {
  return groupAction('leave', groupId)
}

export function moveMembership <T extends Group | SerializedGroup> (group: T, userId: UserId, previousCategory?: GroupMembershipCategory, newCategory?: GroupMembershipCategory) {
  const membership = findMembership(group, previousCategory, userId)
  if (previousCategory) group[previousCategory] = without(group[previousCategory], membership)
  if (newCategory) group[newCategory] = group[newCategory].concat([ membership ])
  return group
}

export function serializeGroupUser (group: SerializedGroup) {
  const adminsIds = new Set(map(group.admins, 'user'))
  const membersIds = new Set(map(group.members, 'user'))
  const requestedIds = new Set(map(group.requested, 'user'))
  const invitedIds = new Set(map(group.invited, 'user'))
  const declinedIds = new Set(map(group.declined, 'user'))
  return (user: ExtendedUser | UserSearchResult) => {
    return {
      ...serializeUser(user),
      isGroupAdmin: adminsIds.has(user._id),
      isGroupMember: adminsIds.has(user._id) || membersIds.has(user._id),
      requestedToJoinGroup: requestedIds.has(user._id),
      wasInvitedToJoinGroup: invitedIds.has(user._id),
      declinedToJoinGroup: declinedIds.has(user._id),
    }
  }
}

export type SerializedGroupUser = ReturnType<ReturnType<typeof serializeGroupUser>>

export async function resolveToGroup (group: Group | GroupId | GroupSlug) {
  let resolvedGroup
  if (typeof group === 'object' && '_id' in group) {
    resolvedGroup = group
  } else if (typeof group === 'string') {
    if (isGroupId(group)) {
      resolvedGroup = await getGroupById(group as GroupId)
    } else {
      resolvedGroup = await getGroupBySlug(group)
    }
  }
  if (resolvedGroup) return serializeGroup(resolvedGroup)
  else throw newError('can not resolve group', 500, { group })
}
